/**
 * useOrientation - 共享的横屏 / 触屏设备检测（原生 matchMedia 版）
 *
 * 使用模块级 ref 作为唯一真源，多个组件调用 `useOrientation()` 读取同一份状态；
 * 监听器用引用计数挂/摘，避免重复绑定。
 *
 * 判斷改走原生 media query（取代 innerWidth/innerHeight + resize/orientationchange）：
 *   - `(orientation: landscape)` —— 比寬高比較準（不受鍵盤彈出 / 位址列縮放干擾）
 *   - `(pointer: coarse)`        —— 主要輸入為粗指標（觸控）；比 ontouchstart/maxTouchPoints
 *     準（觸控筆電有滑鼠時不會誤判成觸控裝置）
 * MediaQueryList 的 `change` 事件本身就涵蓋方向 / 指標變化，不需另外監聽 resize。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const hasMM = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
const mqLandscape = hasMM ? window.matchMedia('(orientation: landscape)') : null
const mqCoarse = hasMM ? window.matchMedia('(pointer: coarse)') : null

const isLandscape = ref(mqLandscape?.matches ?? false)
const isTouchDevice = ref(mqCoarse?.matches ?? false)
let listenerRefCount = 0

const sync = (): void => {
  isLandscape.value = mqLandscape?.matches ?? false
  isTouchDevice.value = mqCoarse?.matches ?? false
}

const attachListeners = (): void => {
  if (listenerRefCount++ > 0) return
  sync()
  mqLandscape?.addEventListener('change', sync)
  mqCoarse?.addEventListener('change', sync)
}

const detachListeners = (): void => {
  if (--listenerRefCount > 0) return
  mqLandscape?.removeEventListener('change', sync)
  mqCoarse?.removeEventListener('change', sync)
}

export const useOrientation = () => {
  onMounted(attachListeners)
  onBeforeUnmount(detachListeners)

  // 触屏设备 + 横屏：用于「盖住游戏 + 提示翻转」
  const isLandscapeOnTouchDevice = computed(() => isTouchDevice.value && isLandscape.value)

  return { isLandscape, isTouchDevice, isLandscapeOnTouchDevice }
}
